import { InsightsCardsConsumption } from "../insights-cards-consumption/insights-cards-consumption";
import { AlertTriangle, CheckCircle, Info, OctagonAlert } from "lucide-react";
import { useActionsTrack } from "@/_old/features/analytics";
import { useNavigate } from "react-router-dom";
import { useCheckInConfirmedBookings } from "./check-in-confirmed-bookings.hooks";
import { useTranslation } from "react-i18next";
import { InsightsCardsConsumptionSkeleton } from "../insights-cards-consumption/insights-cards-consumption-skeleton";
import { useMemo } from "react";
import { truncateToTwoDecimals } from "@/_old/utils/number";
import { ROUTES } from "@/_old/constants/routes";

export function CheckInConfirmedBookings({
  startDate,
  endDate,
  companyId,
  carousel = false,
}: {
  startDate: string;
  endDate: string;
  companyId: number;
  carousel?: boolean;
}) {
  const { onActionClick } = useActionsTrack();

  const navigate = useNavigate();

  const [translate] = useTranslation(["dashboard-charts"]);

  const { data, isLoading } = useCheckInConfirmedBookings({
    startDate,
    endDate,
    companyId,
  });

  const tag = useMemo((): {
    type: "danger" | "default" | "good" | "warning";
    tag: string;
    description: string;
    icon: React.ReactNode;
  } => {
    if (!data?.percentage || data?.percentage === 0) {
      return {
        type: "default",
        tag: translate("dashboard-good-use-no-use"),
        description: translate("dashboard-good-use-unused-description"),
        icon: <Info size={18} />,
      };
    }

    if (data.percentage > 90) {
      return {
        type: "good",
        tag: translate("dashboard-good-use-good-use"),
        description: translate("dashboard-good-use-good-description"),
        icon: <CheckCircle size={18} />,
      };
    }

    if (data.percentage >= 80) {
      return {
        type: "warning",
        tag: translate("dashboard-good-use-moderate-use"),
        description: translate("dashboard-good-use-moderate-description"),
        icon: <AlertTriangle size={18} />,
      };
    }

    return {
      type: "danger",
      tag: translate("dashboard-good-use-low-use"),
      description: translate("dashboard-good-use-low-description"),
      icon: <OctagonAlert size={18} />,
    };
  }, [data]);

  if (isLoading || !data) {
    return <InsightsCardsConsumptionSkeleton carousel={carousel} />;
  }

  return (
    <InsightsCardsConsumption
      tag={tag.tag}
      hasSubtitleDefaultColor={true}
      description={tag.description}
      subtitle={translate("dashboard-good-use-subtitle")}
      buttonText={translate("dashboard-good-use-view-usage")}
      icon={carousel ? tag.icon : <CheckCircle size={18} />}
      content={
        <div
          dangerouslySetInnerHTML={{
            __html: translate("dashboard-good-use-content", {
              percentage: truncateToTwoDecimals(data.percentage),
            }) as string,
          }}
        />
      }
      emptyContent={
        !data.percentage
          ? (translate("dashboard-good-use-empty-content") as string)
          : undefined
      }
      type={tag.type}
      onClick={() => {
        onActionClick("CLICK_INSIGHT_CHECKIN", "DASHBOARD");
        navigate(ROUTES.INSIGHTS);
      }}
      carousel={carousel}
    />
  );
}
